import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { getPersonalInformation, updatePersonalInformation } from '../../../services/employeeService';
import { useAuth } from '../../../context/AuthContext';
import TextInput from '../../../components/ui/TextInput';
import GenericSelect from '../../../components/ui/GenericSelect';
import Button from '../../../components/ui/Button';

const INITIAL_FORM = {
  gender: null,
  pronoun: null,
  maritalStatus: null,
  spouseWorkingStatus: null,
  numberOfChildren: null,
  nationality: '',
  disabilityLevel: null,
  bloodType: '',
  emergencyContactPerson1: '',
  emergencyContactPhone1: '',
  emergencyContactRelationship1: '',
  emergencyContactPerson2: '',
  emergencyContactPhone2: '',
  emergencyContactRelationship2: '',
  educationLevel: null,
  educationStatus: null,
  lastInstitution: '',
  faculty: '',
  bankAccountNumber: '',
  iban: '',
  bankName: '',
};

export default function PersonalInformationTab({ employeeId }) {
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  const [form, setForm] = useState(INITIAL_FORM);
  const [original, setOriginal] = useState(INITIAL_FORM);
  const [recordId, setRecordId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    getPersonalInformation(employeeId)
      .then((res) => {
        const data = res.data?.data;
        if (data) {
          const values = {
            gender: data.gender ?? null,
            pronoun: data.pronoun ?? null,
            maritalStatus: data.maritalStatus ?? null,
            spouseWorkingStatus: data.spouseWorkingStatus ?? null,
            numberOfChildren: data.numberOfChildren ?? null,
            nationality: data.nationality ?? '',
            disabilityLevel: data.disabilityLevel ?? null,
            bloodType: data.bloodType ?? '',
            emergencyContactPerson1: data.emergencyContactPerson1 ?? '',
            emergencyContactPhone1: data.emergencyContactPhone1 ?? '',
            emergencyContactRelationship1: data.emergencyContactRelationship1 ?? '',
            emergencyContactPerson2: data.emergencyContactPerson2 ?? '',
            emergencyContactPhone2: data.emergencyContactPhone2 ?? '',
            emergencyContactRelationship2: data.emergencyContactRelationship2 ?? '',
            educationLevel: data.educationLevel ?? null,
            educationStatus: data.educationStatus ?? null,
            lastInstitution: data.lastInstitution ?? '',
            faculty: data.faculty ?? '',
            bankAccountNumber: data.bankAccountNumber ?? '',
            iban: data.iban ?? '',
            bankName: data.bankName ?? '',
          };
          setForm(values);
          setOriginal(values);
          setRecordId(data.id);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [employeeId]);

  const isDirty = JSON.stringify(form) !== JSON.stringify(original);

  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!recordId || !isDirty) return;
    setSaving(true);
    try {
      await updatePersonalInformation(recordId, form);
      setOriginal(form);
    } catch {
      // handled by interceptor
    } finally {
      setSaving(false);
    }
  }, [recordId, form, isDirty]);

  const genderOptions = [
    { id: 1, name: t('employee.male') },
    { id: 2, name: t('employee.female') },
    { id: 3, name: t('employee.preferNotToSay') },
  ];

  const pronounOptions = [
    { id: 1, name: 'He/Him/His' },
    { id: 2, name: 'She/Her/Hers' },
    { id: 3, name: 'They/Them/Theirs' },
    { id: 4, name: t('employee.preferNotToSay') },
  ];

  const maritalOptions = [
    { id: 1, name: t('employee.married') },
    { id: 2, name: t('employee.single') },
    { id: 3, name: t('employee.divorced') },
  ];

  const yesNoOptions = [
    { id: 1, name: t('employee.yes') },
    { id: 0, name: t('employee.no') },
  ];

  const disabilityOptions = [
    { id: 0, name: '0' },
    { id: 1, name: '1' },
    { id: 2, name: '2' },
    { id: 3, name: '3' },
  ];

  const childrenOptions = Array.from({ length: 11 }, (_, i) => ({ id: i, name: `${i}` }));

  const educationLevelOptions = [
    { id: 1, name: t('employee.eduPrimary') },
    { id: 2, name: t('employee.eduSecondary') },
    { id: 3, name: t('employee.eduHighSchool') },
    { id: 4, name: t('employee.eduAssociate') },
    { id: 5, name: t('employee.eduBachelor') },
    { id: 6, name: t('employee.eduMaster') },
    { id: 7, name: t('employee.eduDoctorate') },
  ];

  const educationStatusOptions = [
    { id: 1, name: t('employee.eduInProgress') },
    { id: 2, name: t('employee.eduGraduated') },
    { id: 3, name: t('employee.eduAbandoned') },
  ];

  const bloodTypeOptions = [
    { id: 'A+', name: 'A+' },
    { id: 'A-', name: 'A-' },
    { id: 'B+', name: 'B+' },
    { id: 'B-', name: 'B-' },
    { id: 'AB+', name: 'AB+' },
    { id: 'AB-', name: 'AB-' },
    { id: '0+', name: '0+' },
    { id: '0-', name: '0-' },
  ];

  if (loading) return <LoadingText>{t('common.loading') || '...'}</LoadingText>;

  const readOnly = !canWrite('EM');

  return (
    <Container>
      <SectionCard>
        <SectionTitle>{t('employee.personalInfo')}</SectionTitle>
        <FormGrid>
          <GenericSelect
            label={t('employee.gender')}
            value={form.gender}
            onChange={(val) => handleChange('gender', val)}
            options={genderOptions}
            idKey="id"
            labelKey="name"
            disabled={readOnly}
          />
          <GenericSelect
            label={t('employee.pronoun')}
            value={form.pronoun}
            onChange={(val) => handleChange('pronoun', val)}
            options={pronounOptions}
            idKey="id"
            labelKey="name"
            disabled={readOnly}
          />
          <GenericSelect
            label={t('employee.maritalStatus')}
            value={form.maritalStatus}
            onChange={(val) => handleChange('maritalStatus', val)}
            options={maritalOptions}
            idKey="id"
            labelKey="name"
            disabled={readOnly}
          />
          <GenericSelect
            label={t('employee.spouseWorking')}
            value={form.spouseWorkingStatus}
            onChange={(val) => handleChange('spouseWorkingStatus', val)}
            options={yesNoOptions}
            idKey="id"
            labelKey="name"
            disabled={readOnly}
          />
          <GenericSelect
            label={t('employee.numberOfChildren')}
            value={form.numberOfChildren}
            onChange={(val) => handleChange('numberOfChildren', val)}
            options={childrenOptions}
            idKey="id"
            labelKey="name"
            disabled={readOnly}
          />
          <TextInput
            label={t('employee.nationality')}
            value={form.nationality}
            onChange={(e) => handleChange('nationality', e.target.value)}
            disabled={readOnly}
          />
          <GenericSelect
            label={t('employee.disabilityLevel')}
            value={form.disabilityLevel}
            onChange={(val) => handleChange('disabilityLevel', val)}
            options={disabilityOptions}
            idKey="id"
            labelKey="name"
            disabled={readOnly}
          />
        </FormGrid>
      </SectionCard>

      <SectionCard>
        <SectionTitle>{t('employee.health')}</SectionTitle>
        <FormGrid>
          <GenericSelect
            label={t('employee.bloodType')}
            value={form.bloodType}
            onChange={(val) => handleChange('bloodType', val)}
            options={bloodTypeOptions}
            idKey="id"
            labelKey="name"
            disabled={readOnly}
          />
        </FormGrid>
      </SectionCard>

      <SectionCard>
        <SectionTitle>{t('employee.emergencyContact1')}</SectionTitle>
        <FormGrid>
          <TextInput
            label={t('employee.contactPerson')}
            value={form.emergencyContactPerson1}
            onChange={(e) => handleChange('emergencyContactPerson1', e.target.value)}
            disabled={readOnly}
          />
          <TextInput
            label={t('employee.contactPhone')}
            value={form.emergencyContactPhone1}
            onChange={(e) => handleChange('emergencyContactPhone1', e.target.value)}
            disabled={readOnly}
          />
          <TextInput
            label={t('employee.contactRelationship')}
            value={form.emergencyContactRelationship1}
            onChange={(e) => handleChange('emergencyContactRelationship1', e.target.value)}
            disabled={readOnly}
          />
        </FormGrid>
      </SectionCard>

      <SectionCard>
        <SectionTitle>{t('employee.emergencyContact2')}</SectionTitle>
        <FormGrid>
          <TextInput
            label={t('employee.contactPerson')}
            value={form.emergencyContactPerson2}
            onChange={(e) => handleChange('emergencyContactPerson2', e.target.value)}
            disabled={readOnly}
          />
          <TextInput
            label={t('employee.contactPhone')}
            value={form.emergencyContactPhone2}
            onChange={(e) => handleChange('emergencyContactPhone2', e.target.value)}
            disabled={readOnly}
          />
          <TextInput
            label={t('employee.contactRelationship')}
            value={form.emergencyContactRelationship2}
            onChange={(e) => handleChange('emergencyContactRelationship2', e.target.value)}
            disabled={readOnly}
          />
        </FormGrid>
      </SectionCard>

      <SectionCard>
        <SectionTitle>{t('employee.education')}</SectionTitle>
        <FormGrid>
          <GenericSelect
            label={t('employee.educationLevel')}
            value={form.educationLevel}
            onChange={(val) => handleChange('educationLevel', val)}
            options={educationLevelOptions}
            idKey="id"
            labelKey="name"
            disabled={readOnly}
          />
          <GenericSelect
            label={t('employee.educationStatus')}
            value={form.educationStatus}
            onChange={(val) => handleChange('educationStatus', val)}
            options={educationStatusOptions}
            idKey="id"
            labelKey="name"
            disabled={readOnly}
          />
          <TextInput
            label={t('employee.lastInstitution')}
            value={form.lastInstitution}
            onChange={(e) => handleChange('lastInstitution', e.target.value)}
            disabled={readOnly}
          />
          <TextInput
            label={t('employee.faculty')}
            value={form.faculty}
            onChange={(e) => handleChange('faculty', e.target.value)}
            disabled={readOnly}
          />
        </FormGrid>
      </SectionCard>

      {canWrite('EM') && (
        <SectionCard>
          <SectionTitle>{t('employee.banking')}</SectionTitle>
          <FormGrid>
            <TextInput
              label={t('employee.bankName')}
              value={form.bankName}
              onChange={(e) => handleChange('bankName', e.target.value)}
            />
            <TextInput
              label={t('employee.bankAccountNumber')}
              value={form.bankAccountNumber}
              onChange={(e) => handleChange('bankAccountNumber', e.target.value)}
            />
            <TextInput
              label={t('employee.iban')}
              value={form.iban}
              onChange={(e) => handleChange('iban', e.target.value)}
            />
          </FormGrid>
        </SectionCard>
      )}

      {canWrite('EM') && (
        <SaveRow>
          <Button
            variant="primary"
            disabled={!isDirty || saving}
            onClick={handleSave}
          >
            {saving ? '...' : t('common.save') || 'Save'}
          </Button>
        </SaveRow>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const LoadingText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #A0A8B4;
  padding: 40px;
  text-align: center;
`;

const SectionCard = styled.div`
  padding: 20px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #E8ECF0;
`;

const SectionTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 16px 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const SaveRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;
